import { useQuery } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SSpanTitleSmall } from '~/components/styles/styles';
import { LeaderBoardHeadItem } from '~/components/ui/leaderboard-head-item';
import { Loading } from '~/components/ui/loading';
import ModalListAuto from '~/components/ui/modal-list-auto';
import { WrapperListAuto } from '~/components/ui/wrapper-list-auto';
import { GQL_LEADERBOARD, StoredLeaderoard } from '~/gql/users/users';
import { authClient } from '~/helpers/apollo';
import { classic } from '~/themes';
import { renderFullPath } from '~/utils/common/utils';

interface IProps {
  open: boolean;
  onClose: () => void;
}
type TProps = IProps;

export default function LeaderBoardDialog({ open, onClose }: TProps) {
  const { i18n } = useLingui();
  const [loading, setLoading] = useState<boolean>(false);
  const [leaderBoardUsers, setLeaderBoardUsers] = useState<StoredLeaderoard[]>(
    []
  );

  const { refetch: fetchLeaderBoard } = useQuery(GQL_LEADERBOARD, {
    client: authClient,
    onCompleted: (data) => {
      setLeaderBoardUsers(data.getLeaderboard);
    },
  });

  async function getLeaderBoard() {
    setLoading(true);
    await fetchLeaderBoard();
    setLoading(false);
  }

  useEffect(() => {
    void getLeaderBoard();
  }, []);

  return (
    <ModalListAuto onClose={onClose} open={open}>
      <WrapperListAuto
        onClose={onClose}
        title={`${i18n._('common.leaderBoard')}`}
        headerChildren={
          <>
            <SSpanTitleSmall
              $marginBottom="5px"
              $color={classic.token?.colorWhite}
            >
              {i18n._('mode.solo')}
            </SSpanTitleSmall>

            {leaderBoardUsers.length > 0 && (
              <SDivHeader>
                <SWrapperItem>
                  {leaderBoardUsers[1] && (
                    <LeaderBoardHeadItem
                      avatarSrc={leaderBoardUsers[1].avatar?.fileName}
                      name={leaderBoardUsers[1].username}
                      position={leaderBoardUsers[1].score}
                      size="md"
                      nb={1}
                    />
                  )}
                </SWrapperItem>
                <SWrapperItem>
                  {leaderBoardUsers[0] && (
                    <LeaderBoardHeadItem
                      avatarSrc={leaderBoardUsers[0].avatar?.fileName}
                      name={leaderBoardUsers[0].username}
                      position={leaderBoardUsers[0].score}
                      size="lg"
                      nb={0}
                    />
                  )}
                </SWrapperItem>
                <SWrapperItem>
                  {leaderBoardUsers[2] && (
                    <LeaderBoardHeadItem
                      avatarSrc={leaderBoardUsers[2].avatar?.fileName}
                      name={leaderBoardUsers[2].username}
                      position={leaderBoardUsers[2].score}
                      size="sm"
                      nb={2}
                    />
                  )}
                </SWrapperItem>
              </SDivHeader>
            )}
          </>
        }
      >
        {loading ? (
          <Loading />
        ) : (
          <Container>
            <STableWrapper>
              <STable>
                <tbody>
                  {leaderBoardUsers.map((user, index) => (
                    <tr key={index}>
                      <td>{user.position}</td>
                      <td>
                        {user.avatar?.fileName && (
                          <SDivAvatar>
                            <img
                              src={renderFullPath(user.avatar?.fileName)}
                              alt=""
                            />
                          </SDivAvatar>
                        )}
                      </td>
                      <td>{user.username}</td>
                      <td>
                        {user.score}
                        {' %'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </STable>
            </STableWrapper>
          </Container>
        )}
      </WrapperListAuto>
    </ModalListAuto>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const SDivHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const SWrapperItem = styled.div`
  margin: 0 10px;
  width: 25%;
`;

const SDivAvatar = styled.div`
  border-radius: 50%;
  background-color: #fff;
  height: 40px;
  width: 44px;
  position: relative;
  border: 3px solid #fff;

  img {
    position: absolute;
    object-fit: contain;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
`;

const STableWrapper = styled.div`
  flex-grow: 1;
  width: 100%;
  overflow-y: auto;
  padding: 20px 20px 330px 20px;

  @media screen and (max-height: 800px) {
    padding: 10px 10px 300px 10px;
  }

  @media screen and (max-height: 450px) {
    padding: 10px 10px 300px 10px;
  }

  @media screen and (max-width: 450px) {
    padding: 10px 10px 300px 10px;
  }
`;

const STable = styled.table`
  text-align: left;
  position: relative;
  border-collapse: collapse;
  width: 100%;

  tbody {
    tr {
      &:nth-of-type(even) {
        background-color: #ffffff80;
        user-select: none;
      }
      &:nth-of-type(odd) {
        background-color: transparent;
        user-select: none;
      }
      td {
        color: #113969;
        font-family: 'Riffic Free', sans-serif;
        background-color: transparent;
        font-weight: bold;
        font-size: 1.1rem;
        padding: 8px 12px;
        user-select: none;

        @media screen and (max-width: 450px) {
          font-size: 0.9rem;
          padding: 5px 8px;
        }
        @media screen and (max-width: 290px) {
          font-size: 0.8rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 600px) {
          font-size: 0.9rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 450px) {
          font-size: 0.8rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 290px) {
          font-size: 0.7rem;
          padding: 5px 8px;
        }

        &:first-of-type {
          text-align: center;
          width: 10%;
          user-select: none;
        }
        &:nth-of-type(2) {
          width: 10%;
          user-select: none;
        }
        &:nth-of-type(3) {
          width: 40%;
          user-select: none;
        }
        &:last-of-type {
          padding-right: 10px;
          text-align: right;
          width: 20%;
          user-select: none;
        }
      }
      &:hover {
        background-color: rgba(83, 210, 215, 0.2);
      }
    }
  }
`;
